// Third party library imports
import { Injectable } from '@angular/core';
import Keycloak from 'keycloak-js';

// Zopup library imports
import { StorageKey, StorageService } from '@zopup/zop-shared';
import {
    EmailAddressDto,
    PhoneNumberDto,
    TenantCompactDto,
    UserDto,
    VerificationStatus,
} from '@zopup/zop-types';

// Internal project imports
import { AuthUtils } from '../../utils/auth.utils';

@Injectable({ providedIn: 'root' })
export class TokenParserService {
    constructor(private _storageService: StorageService) {}

    /**
     * Parse the Keycloak token and extract tenant organizations
     * @param token The decoded Keycloak token
     * @returns Array of TenantCompactDto
     */
    parseTokenTenants(token: any): TenantCompactDto[] {
        if (!token || !token.organization) {
            return [];
        }

        // List of attributes that should always be arrays
        const ALWAYS_ARRAYS: string[] = [];
        const organizations = AuthUtils.normalizeAttributes(token.organization, '', ALWAYS_ARRAYS)[0];
        const tenants: TenantCompactDto[] = [];

        // Convert organization object to array of TenantCompactDto
        Object.keys(organizations).forEach(orgName => {
            const orgData = organizations[orgName];
            tenants.push({
                id: orgData.id,
                name: orgData.name,
                invitationStatus: 'Accepted', // Default status for tokens
                roles: [] // Could be populated from other token data if available
            });
        });

        return tenants;
    }

    /**
     * Parse Keycloak profile and convert to UserDto
     * Stores the result in storage
     * @param keycloak The Keycloak instance
     * @returns Promise<UserDto> The parsed user profile
     */
    async parseUserProfile(keycloak: Keycloak): Promise<UserDto> {
        // Load profile if not already loaded
        if (!keycloak.profile) {
            await keycloak.loadUserProfile();
        }

        // Create new UserDto from Keycloak profile
        const userDto = new UserDto();
        const profile = keycloak.profile;

        // Map standard fields
        userDto.id = profile.id || '';
        userDto.uId = profile.id || ''; // Using ID as UID as well
        userDto.firstName = profile.firstName || '';
        userDto.lastName = profile.lastName || '';
        userDto.displayName = profile.username || `${userDto.firstName} ${userDto.lastName}`.trim();

        // Map email
        if (profile.email) {
            userDto.email = {
                label: 'Primary',
                value: profile.email,
                verificationStatus: profile.emailVerified ? VerificationStatus.Verified : VerificationStatus.Pending
            } as EmailAddressDto;
            userDto.emailVerified = profile.emailVerified || false;
        }

        // Map phone number if available
        if (profile.attributes && profile.attributes.hasOwnProperty('phoneNumber')) {
            userDto.mobileNumber = {
                label: 'Primary',
                value: profile.attributes['phoneNumber'][0] || '',
            } as PhoneNumberDto;
        }

        // Get tenants from storage that were already parsed from token
        const tenants = this._storageService.getItem<TenantCompactDto[]>(StorageKey.tenants);
        if (tenants && tenants.length > 0) {
            userDto.tenants = tenants;
        }

        // Store user in storage
        this._storageService.user = userDto;

        return userDto;
    }

    /**
     * Parse the complete token and store all relevant information
     * @param keycloak The Keycloak instance
     * @returns Promise<void>
     */
    async parseToken(keycloak: Keycloak): Promise<void> {
        if (!keycloak.token) {
            console.error('No Keycloak token available to parse');
            return;
        }

        console.log('🔍 TokenParserService: Parsing token...');
        const token = AuthUtils.decodeToken(keycloak.token);

        // Parse and store tenant organizations
        const tenants = this.parseTokenTenants(token);
        if (tenants && tenants.length > 0) {
            console.log('💾 TokenParserService: Storing tenants:', tenants.length);
            this._storageService.setItem(StorageKey.tenants, tenants);
        }

        // Parse and store user profile
        await this.parseUserProfile(keycloak);
        console.log('✅ TokenParserService: Token parsing completed');
    }
}
